import { sharedLib } from "@shared/services/shared.service.ts";
import { ColumnsType } from "antd/es/table";
import Table from "common/Table/Table.tsx";
import React, { useMemo } from "react";
import {
  ExternalAccountInvitation,
  LanguageNameSpaces,
  ExternalInvitationType,
  UserImpersonationPermissionLevel,
} from "@shared/types";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general.ts";
import InvitationActions from "features/configuration/InviteExternalUsers/InvitationActions.tsx";
import { formatDate } from "@shared/utils/date";
import Text from "common/Typography/Text";
import { DELETE_EXTERNAL_INVITATION_CACHE_KAY } from "features/configuration/InviteExternalUsers/constants.ts";
import InvitationStatusTag from "features/configuration/InviteExternalUsers/InvitationStatusTag.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { INVITATION_PERMISSION_TO_DISPLAY } from "features/configuration/common/constants.ts";
import { useShowPermissionOptions } from "features/configuration/InviteExternalUsers/hooks.tsx";
import { useTheme } from "styled-components";
import EmptyView from "common/Empty/EmptyView";
import { EmptyStatesType } from "common/Icons/types.ts";

const ManageInvitationsTable = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isNewLook } = useTheme();
  const { data, isLoading } =
    sharedLib.sharedApi.configuration.useGetAccountInvitesQuery();
  const deleteAccountInviteStatus =
    sharedLib.sharedApi.configuration.useDeleteAccountInviteMutation({
      fixedCacheKey: DELETE_EXTERNAL_INVITATION_CACHE_KAY,
    })[1];
  const { showAssetManagerPermissionOptions, showClientPermissionOptions } =
    useShowPermissionOptions();

  const getFormattedTime = (timestamp: number) => {
    return formatDate(timestamp, true, true);
  };

  const columns: ColumnsType<ExternalAccountInvitation> = [
    {
      title: t("invitations.client_name"),
      dataIndex: "name",
      showSorterTooltip: false,
      render: (name) => (
        <Text $ellipsis={true} style={{ maxWidth: MAX_NAME_WIDTH }}>
          {name}
        </Text>
      ),
      width: MAX_NAME_WIDTH,
      sorter: (a, b) => sortByStringProperty(a, b, "name"),
    },
    {
      title: t("invitations.creation"),
      dataIndex: "creation_datetime",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      render: getFormattedTime,
      sorter: (a, b) => sortByNumberProperty(a, b, "creation_datetime"),
    },
    {
      title: t("invitations.expiration"),
      dataIndex: "valid_to",
      showSorterTooltip: false,
      render: getFormattedTime,
      sorter: (a, b) => sortByNumberProperty(a, b, "valid_to"),
    },
    {
      title: t("invitations.status"),
      dataIndex: "status",
      render: (_, invitation) => (
        <InvitationStatusTag invitation={invitation} />
      ),
    },
    {
      title: "",
      dataIndex: "id",
      key: "action",
      width: 40,
      render: (_, inviteData) => (
        <InvitationActions invitationData={inviteData} />
      ),
    },
  ];

  if (showClientPermissionOptions) {
    columns.splice(4, 0, {
      title: t("invitations.client_access"),
      dataIndex: "client_access",
      render: (_, invitation) =>
        INVITATION_PERMISSION_TO_DISPLAY[
          invitation.permission_level ?? UserImpersonationPermissionLevel.NONE
        ],
    });
  }
  if (showAssetManagerPermissionOptions) {
    columns.splice(5, 0, {
      title: t("invitations.manager_access"),
      dataIndex: "manager_access",
      render: (_, invitation) =>
        INVITATION_PERMISSION_TO_DISPLAY[
          invitation.asset_manager_permission_level ??
            UserImpersonationPermissionLevel.NONE
        ],
    });
  }

  const filteredData = useMemo(
    () =>
      data?.filter(
        (invitation) => invitation.type !== ExternalInvitationType.PERSONAL
      ),
    [data]
  );

  return (
    <Table<ExternalAccountInvitation>
      columns={columns}
      dataSource={filteredData}
      loading={isLoading || deleteAccountInviteStatus.isLoading}
      rowClassName="table-half-padding"
      rowKey={(invitation) => invitation.token}
      locale={
        isNewLook
          ? {
              emptyText: (
                <EmptyView
                  iconKey={EmptyStatesType.Users}
                  iconWidth={110}
                  title={tCommon("empty.no_data")}
                  style={NEW_LOOK_EMPTY_STYLE}
                />
              ),
            }
          : undefined
      }
    />
  );
};

export default ManageInvitationsTable;

const MAX_NAME_WIDTH = 180;
const NEW_LOOK_EMPTY_STYLE = { padding: "24px 0" };
